import React, { Suspense, useEffect, useState } from 'react'
import { Button, Image, Table } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import DeleteIcon from '../../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../../components/AllSvgIcon/EditIcon'
import Previewicon from '../../../../components/AllSvgIcon/Previewicon'
import HKBadge from '../../../../components/Badges'
import Loader from '../../../../components/Loader'
import SearchBox from '../../../../components/Searchbox'
import CustomToast from '../../../../components/Toast/CustomToast'
import {
  calculatePageRange,
  encodedSearchText,
  showToast
} from '../../../../lib/AllGlobalFunction'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../../lib/AllPageNames'
import { pageRangeDisplayed } from '../../../../lib/AllStaticVariables'
import axiosProvider from '../../../../lib/AxiosProvider'
import { _categoryImg_ } from '../../../../lib/ImagePath'
import { _SwalDelete, _exception } from '../../../../lib/exceptionMessage'
import useDebounce from '../../../../lib/useDebounce'

const MainCategoryForm = React.lazy(() => import('./MainCategoryForm'))
const MainCategoryDetails = React.lazy(() => import('./MainCategoryDetails'))

const MainCategoryList = () => {
  const [searchText, setSearchText] = useState('')
  const [data, setData] = useState()
  const [modalShow, setModalShow] = useState({ show: false, type: '' })
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })
  const [filterDetails, setFilterDetails] = useImmer({
    pageSize: 50,
    pageIndex: 1,
    searchText: ''
  })
  const { pageAccess, userInfo } = useSelector((state) => state.user)
  const initVal = {
    name: '',
    filename: null,
    metaTitles: '',
    metaKeywords: [],
    metaDescription: '',
    status: '',
    color: '#000000',
    title: '',
    subTitle: '',
    description: ''
  }
  const [initialValues, setInitialValues] = useState(initVal)
  const debounceSearchText = useDebounce(searchText, 500)
  const location = useLocation()

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'MainCategory/search',
        queryString: `?searchText=${encodedSearchText(
          filterDetails?.searchText
        )}&pageIndex=${filterDetails?.pageIndex}&pageSize=${
          filterDetails?.pageSize
        }`
      })
      setLoading(false)
      if (response?.status === 200) {
        setData(response)
      }
    } catch {
      setLoading(false)
    }
  }

  const handlePageClick = (event) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setFilterDetails((draft) => {
      draft.pageIndex = event.selected + 1
    })
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: 'MainCategory',
        queryString: `?Id=${id}`,
        userId: userInfo?.userId,
        location: location.pathname
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        fetchData()
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [filterDetails])

  useEffect(() => {
    if (debounceSearchText) {
      setFilterDetails((draft) => {
        draft.searchText = debounceSearchText
        draft.pageIndex = 1
      })
    } else {
      setFilterDetails((draft) => {
        draft.searchText = ''
        draft.pageIndex = 1
      })
    }
  }, [debounceSearchText])

  return (
    <>
      <div className='d-flex align-items-center mb-3 gap-3 flex-row-reverse'>
        {checkPageAccess(
          pageAccess,
          allPages?.category,
          allCrudNames?.write
        ) && (
          <Button
            variant='warning'
            className='d-flex align-items-center gap-2 fw-semibold btn btn-warning ms-auto'
            onClick={() => {
              setInitialValues(initVal)
              setModalShow({ show: true, type: 'form' })
            }}
          >
            <i className='m-icon m-icon--plusblack'></i>
            Create
          </Button>
        )}

        <div className='page-range'>
          {calculatePageRange({
            ...filterDetails,
            recordCount: data?.data?.pagination?.recordCount ?? 0
          })}
        </div>

        <div className='d-flex align-items-center'>
          <label className='me-1'>Show</label>
          <select
            name='dataget'
            id='parpageentries'
            value={filterDetails?.pageSize}
            className='form-select me-1'
            onChange={(e) => {
              setFilterDetails((draft) => {
                draft.pageSize = e?.target?.value
                draft.pageIndex = 1
              })
            }}
          >
            <option value='10'>10</option>
            <option value='25'>25</option>
            <option value='50'>50</option>
            <option value='100'>100</option>
            <option value='200'>200</option>
            <option value='500'>500</option>
          </select>
        </div>

        <SearchBox
          placeholderText={'Search'}
          searchclassnamewrapper={'searchbox-wrapper'}
          value={searchText}
          onChange={(e) => {
            setSearchText(e?.target?.value)
          }}
        />
      </div>

      <Table responsive className='align-middle table-list'>
        <thead>
          <tr>
            <th>Categories</th>
            <th>Color</th>
            <th>Status</th>
            <th className='text-center'>Action</th>
          </tr>
        </thead>
        <tbody>
          {data?.data?.data?.length > 0 ? (
            data?.data?.data?.map((category, index) => (
              <tr key={`category.name + ${Math.random()}`}>
                <td>
                  <div className='d-flex gap-2 align-items-center'>
                    <Image
                      src={
                        category?.image
                          ? `${process.env.REACT_APP_IMG_URL}${_categoryImg_}${category?.image}`
                          : 'https://placehold.jp/50x50.png'
                      }
                      className='img-thumbnail table-img-box'
                    />
                    <span>{category.name}</span>
                  </div>
                </td>
                <td>
                  {category?.color ? (
                    <div className='d-flex align-items-center gap-2'>
                      <span
                        className='d-inline-block rounded cw-25 ch-25 border border-1'
                        style={{ backgroundColor: `${category?.color}` }}
                      ></span>
                      {category?.color}
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <HKBadge
                    badgesbgname={
                      category.status === 'Active' ? 'success' : 'danger'
                    }
                    badgesTxtname={category.status}
                    badgeClassname={''}
                  />
                </td>
                <td className='text-center'>
                  <div className='d-flex gap-2 justify-content-center'>
                    <span
                      onClick={() => {
                        setModalShow({ show: true, type: 'details' })
                        setInitialValues(category)
                      }}
                    >
                      <Previewicon bg={'bg'} />
                    </span>
                    {checkPageAccess(
                      pageAccess,
                      allPages?.category,
                      allCrudNames?.update
                    ) && (
                      <span
                        onClick={() => {
                          setModalShow({ show: true, type: 'form' })
                          setInitialValues({
                            ...category,
                            metaKeywords: category?.metaKeywords
                              ? category?.metaKeywords?.split(',')
                              : []
                          })
                        }}
                      >
                        <EditIcon bg={'bg'} />
                      </span>
                    )}

                    {checkPageAccess(
                      pageAccess,
                      allPages?.category,
                      allCrudNames?.delete
                    ) && (
                      <span
                        onClick={() => {
                          Swal.fire({
                            title: _SwalDelete.title,
                            text: _SwalDelete.text,
                            icon: _SwalDelete.icon,
                            showCancelButton: _SwalDelete.showCancelButton,
                            confirmButtonColor: _SwalDelete.confirmButtonColor,
                            cancelButtonColor: _SwalDelete.cancelButtonColor,
                            confirmButtonText: _SwalDelete.confirmButtonText,
                            cancelButtonText: _SwalDelete.cancelButtonText
                          }).then((result) => {
                            if (result.isConfirmed) {
                              handleDelete(category.id)
                            } else if (result.isDenied) {
                            }
                          })
                        }}
                      >
                        <DeleteIcon bg={'bg'} />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className='text-center'>
                {data?.data?.message}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {data?.data?.pagination?.pageCount > 0 && (
        <ReactPaginate
          className='list-inline m-cst--pagination d-flex justify-content-end gap-1'
          breakLabel='...'
          nextLabel=''
          onPageChange={handlePageClick}
          pageRangeDisplayed={pageRangeDisplayed}
          pageCount={data?.data?.pagination?.pageCount}
          previousLabel=''
          renderOnZeroPageCount={null}
          forcePage={filterDetails?.pageIndex - 1}
        />
      )}

      {toast?.show && !modalShow?.show && (
        <CustomToast text={toast?.text} variation={toast?.variation} />
      )}

      {loading && !modalShow?.show && <Loader />}

      <Suspense fallback={<Loader />}>
        {modalShow?.show && modalShow?.type === 'form' && (
          <MainCategoryForm
            initialValues={initialValues}
            modalShow={modalShow}
            setModalShow={setModalShow}
            loading={loading}
            setLoading={setLoading}
            fetchData={fetchData}
            toast={toast}
            setToast={setToast}
          />
        )}

        {modalShow?.show && modalShow?.type === 'details' && (
          <MainCategoryDetails
            initialValues={initialValues}
            setModalShow={setModalShow}
            modalShow={modalShow}
          />
        )}
      </Suspense>
    </>
  )
}

export default MainCategoryList
